import React, { useCallback, useEffect, useState } from 'react';
import { fetchPortalAccount, fetchPortalActivity, updatePortalAccount } from '../api';
import FormSetup from './FormSetup';
import People from './People';

const TABS = [
  { key: 'settings', label: 'Settings' },
  { key: 'form', label: 'Job form' },
  { key: 'people', label: 'People' },
  { key: 'activity', label: 'Activity' },
];

const SETTING_KEYS = ['is_enabled', 'features', 'default_intake', 'allow_intake_choice', 'default_contact',
  'skip_incoming', 'send_job_emails', 'job_email_events'];

export const errorText = (err, fallback) => {
  const data = err.response?.data;
  if (data?.errors) return Object.values(data.errors).join(' ');
  return data?.detail || fallback;
};

export const when = (iso) => (iso ? new Date(iso).toLocaleString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '—');

function Toggle({ checked, onChange, label, help }) {
  return (
    <label className="pa-check">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>
        <span className="pa-check-label">{label}</span>
        {help && <span className="pa-help">{help}</span>}
      </span>
    </label>
  );
}

function Settings({ account, options, onSaved }) {
  const [draft, setDraft] = useState(() => Object.fromEntries(SETTING_KEYS.map((k) => [k, account[k]])));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const set = (key, value) => { setDraft((d) => ({ ...d, [key]: value })); setMessage(''); };
  const dirty = SETTING_KEYS.some((k) => JSON.stringify(draft[k]) !== JSON.stringify(account[k]));

  const toggleFeature = (key, on) => set('features', on
    ? options.features.map((f) => f.key).filter((k) => k === key || draft.features.includes(k))
    : draft.features.filter((k) => k !== key));

  const emailEvents = draft.job_email_events || [];
  const toggleEmail = (key, on) => set('job_email_events', on
    ? options.email_events.map((e) => e.key).filter((k) => k === key || emailEvents.includes(k))
    : emailEvents.filter((k) => k !== key));

  const save = async () => {
    setSaving(true);
    try {
      onSaved(await updatePortalAccount(account.id, draft));
      setMessage('Saved.');
    } catch (err) {
      setMessage(errorText(err, 'Could not save.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pa-card">
      <Toggle
        checked={draft.is_enabled}
        onChange={(v) => set('is_enabled', v)}
        label="Portal on"
        help="Off: nobody at this business can sign in. Anyone signed in only here is signed out."
      />

      <h4>What this business can do</h4>
      <p className="pa-help">Each person also needs it turned on for them (People tab).</p>
      {options.features.map((f) => (
        <Toggle key={f.key} checked={draft.features.includes(f.key)} onChange={(v) => toggleFeature(f.key, v)}
                label={f.label} help={f.help} />
      ))}

      <h4>New jobs</h4>
      <div className="pa-row">
        <label className="pa-field">
          <span>Usually</span>
          <select className="pa-input" value={draft.default_intake} onChange={(e) => set('default_intake', e.target.value)}>
            {options.intakes.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="pa-field">
          <span>Contact on their jobs</span>
          <select className="pa-input" value={draft.default_contact} onChange={(e) => set('default_contact', e.target.value)}>
            <option value="">None (the shop assigns)</option>
            {options.contacts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span className="pa-help">Customers never see the contact.</span>
        </label>
      </div>
      <Toggle checked={draft.allow_intake_choice} onChange={(v) => set('allow_intake_choice', v)}
              label="They can switch between pickup and drop-off on each job" />
      <Toggle checked={draft.skip_incoming} onChange={(v) => set('skip_incoming', v)}
              label="Jobs go straight into the job list"
              help="Off: their jobs wait in Incoming until someone taps Picked up / Received." />

      <h4>Emails</h4>
      <Toggle checked={draft.send_job_emails} onChange={(v) => set('send_job_emails', v)}
              label="Email them about their jobs"
              help="Who gets them is set per person on the People tab." />
      {draft.send_job_emails && (
        <div className="pa-indent">
          <p className="pa-help">Which emails:</p>
          {options.email_events.map((e) => (
            <Toggle key={e.key} checked={emailEvents.includes(e.key)} onChange={(v) => toggleEmail(e.key, v)}
                    label={e.label} help={e.help} />
          ))}
          {!emailEvents.length && <p className="pa-warning">None ticked: they won't get any job emails.</p>}
        </div>
      )}

      <div className="pa-actions">
        <button type="button" className="pa-button" disabled={!dirty || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {message && <span className="pa-message">{message}</span>}
      </div>
    </div>
  );
}

function Activity({ accountId }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    fetchPortalActivity(accountId).then(setRows).catch(() => setError('Could not load activity.'));
  }, [accountId]);
  if (error) return <div className="pa-error">{error}</div>;
  if (!rows) return <div className="pa-muted">Loading…</div>;
  if (!rows.length) return <div className="pa-empty">Nothing yet.</div>;
  return (
    <div className="pa-card">
      <table className="pa-table">
        <thead><tr><th>When</th><th>Who</th><th>What</th><th>Details</th></tr></thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id}>
              <td>{when(a.created_at)}</td>
              <td>{a.person || a.staff}</td>
              <td>{a.label}</td>
              <td>{a.detail}{a.staff && a.person ? ` (by ${a.staff})` : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AccountEditor({ accountId, options, onChanged }) {
  const [account, setAccount] = useState(null);
  const [tab, setTab] = useState('settings');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    fetchPortalAccount(accountId)
      .then((data) => { setAccount(data); setError(''); })
      .catch(() => setError('Could not load this business.'));
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const saved = (data) => { setAccount(data); onChanged(); };

  if (error) return <div className="pa-error">{error}</div>;
  if (!account) return <div className="pa-muted">Loading…</div>;

  return (
    <div>
      <div className="pa-detail-header">
        <h3>{account.customer_name}</h3>
        {!account.is_enabled && <span className="pa-tag pa-tag-off">Portal off</span>}
      </div>
      <div className="pa-tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
                  className={`pa-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}{t.key === 'people' ? ` (${account.members.length})` : ''}
          </button>
        ))}
      </div>
      {tab === 'settings' && <Settings account={account} options={options} onSaved={saved} />}
      {tab === 'form' && <FormSetup account={account} options={options} onSaved={saved} />}
      {tab === 'people' && <People account={account} options={options} onChanged={() => { load(); onChanged(); }} />}
      {tab === 'activity' && <Activity accountId={account.id} />}
    </div>
  );
}

export default AccountEditor;
